(function() {
  /** @type {WeakMap<HTMLElement, any>} */
  const initializedSliders = new WeakMap();

  /**
   * @param {HTMLElement} container
   */
  function initTabSlider(container) {
    if (!container) return;

    // Skip if already initialized
    if (initializedSliders.has(container)) return;

    const tabsCarouselElement = container.querySelector('.nf-tab-slider__tabs-carousel');
    const contentCarouselElement = container.querySelector('.nf-tab-slider__content-carousel');
    const tabButtons = container.querySelectorAll('.nf-tab-slider__tab');
    const prevButton = container.querySelector('.nf-tab-slider__button--prev');
    const nextButton = container.querySelector('.nf-tab-slider__button--next');
    const dotsContainer = container.querySelector('.nf-tab-slider__dots');

    if (!tabsCarouselElement || !contentCarouselElement || !tabButtons.length) {
      return;
    }

    if (typeof EmblaCarousel === 'undefined') {
      setTimeout(() => initTabSlider(container), 100);
      return;
    }

    // Initialize tabs carousel - one per screen on mobile
    const getTabsAlign = () => {
      return window.matchMedia('(min-width: 768px)').matches ? 'start' : 'center';
    };

    const tabsEmbla = EmblaCarousel(tabsCarouselElement, {
      loop: false,
      align: getTabsAlign(),
      containScroll: 'trimSnaps',
      slidesToScroll: 1,
      dragFree: false
    });

    // Initialize content carousel
    const contentEmbla = EmblaCarousel(contentCarouselElement, {
      loop: false,
      align: 'start',
      containScroll: 'trimSnaps',
      slidesToScroll: 1,
      dragFree: false
    });

    // Setup pagination dots
    const setupDots = () => {
      if (!dotsContainer) return;
      
      dotsContainer.innerHTML = '';
      const slides = contentCarouselElement.querySelectorAll('.nf-tab-slider__content-panel');
      
      slides.forEach((_, index) => {
        const dot = document.createElement('button');
        dot.className = 'nf-tab-slider__dot';
        dot.setAttribute('type', 'button');
        dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
        dot.addEventListener('click', () => {
          switchToSlide(index);
        });
        dotsContainer.appendChild(dot);
      });
    };

    const updateDots = () => {
      if (!dotsContainer) return;
      
      const dots = dotsContainer.querySelectorAll('.nf-tab-slider__dot');
      const selectedIndex = contentEmbla.selectedScrollSnap();
      
      dots.forEach((dot, index) => {
        const isActive = index === selectedIndex;
        dot.classList.toggle('nf-tab-slider__dot--active', isActive);
        dot.setAttribute('aria-selected', String(isActive));
      });
    };

    const updateButtons = () => {
      if (prevButton && prevButton instanceof HTMLElement && 'disabled' in prevButton) {
        (/** @type {HTMLButtonElement} */ (prevButton)).disabled = !contentEmbla.canScrollPrev();
      }
      if (nextButton && nextButton instanceof HTMLElement && 'disabled' in nextButton) {
        (/** @type {HTMLButtonElement} */ (nextButton)).disabled = !contentEmbla.canScrollNext();
      }
    };

    // Add button event listeners
    if (prevButton) {
      prevButton.addEventListener('click', () => {
        contentEmbla.scrollPrev();
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', () => {
        contentEmbla.scrollNext();
      });
    }

    /**
     * @param {number} index
     */
    function switchToSlide(index) {
      // Scroll both carousels to the selected index
      tabsEmbla.scrollTo(index);
      contentEmbla.scrollTo(index);
      updateTabActiveStates(index);
    }

    // Add click handlers to tab buttons
    tabButtons.forEach((button, index) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        switchToSlide(index);
      });

      button.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          switchToSlide(index);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          const nextIndex = (index + 1) % tabButtons.length;
          switchToSlide(nextIndex);
          const nextButton = tabButtons[nextIndex];
          if (nextButton && 'focus' in nextButton) {
            (/** @type {HTMLElement} */ (nextButton)).focus();
          }
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          const prevIndex = (index - 1 + tabButtons.length) % tabButtons.length;
          switchToSlide(prevIndex);
          const prevButton = tabButtons[prevIndex];
          if (prevButton && 'focus' in prevButton) {
            (/** @type {HTMLElement} */ (prevButton)).focus();
          }
        }
      });
    });

    // Update tab active states
    const updateTabActiveStates = (selectedIndex) => {
      tabButtons.forEach((button, i) => {
        const isActive = i === selectedIndex;
        button.classList.toggle('nf-tab-slider__tab--active', isActive);
        button.classList.toggle('opacity-100', isActive);
        button.setAttribute('aria-selected', String(isActive));
        button.setAttribute('tabindex', isActive ? '0' : '-1');
      });
    };

    // Sync content carousel when tabs carousel is scrolled
    tabsEmbla.on('select', () => {
      const selectedIndex = tabsEmbla.selectedScrollSnap();
      contentEmbla.scrollTo(selectedIndex);
      updateTabActiveStates(selectedIndex);
    });

    // Sync tabs carousel when content carousel is scrolled
    contentEmbla.on('select', () => {
      const selectedIndex = contentEmbla.selectedScrollSnap();
      tabsEmbla.scrollTo(selectedIndex);
      updateTabActiveStates(selectedIndex);

      // Update dots and buttons
      updateDots();
      updateButtons();
    });

    // Initialize dots and buttons
    contentEmbla.on('init', () => {
      setupDots();
      updateDots();
      updateButtons();
    });

    // Update on reinit
    contentEmbla.on('reInit', () => {
      setupDots();
      updateDots();
      updateButtons();
    });

    // Initial setup
    setupDots();
    updateDots();
    updateButtons();
    updateTabActiveStates(0);

    // Handle window resize to update tabs carousel alignment
    let resizeTimer;
    const handleResize = () => {
      if (resizeTimer !== undefined) {
        clearTimeout(resizeTimer);
      }
      resizeTimer = setTimeout(() => {
        tabsEmbla.reInit();
      }, 250);
    };
    window.addEventListener('resize', handleResize);

    // Store references
    initializedSliders.set(container, {
      tabsEmbla,
      contentEmbla,
      switchToSlide
    });
  }

  function initAllTabSliders() {
    const containers = document.querySelectorAll('.nf-tab-slider:not([data-initialized])');
    containers.forEach((container) => {
      if (container instanceof HTMLElement) {
        container.setAttribute('data-initialized', 'true');
        initTabSlider(container);
      }
    });
  }

  function handleThemeEditorUpdates() {
    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:load', initAllTabSliders);
      document.addEventListener('shopify:section:reorder', initAllTabSliders);
      document.addEventListener('shopify:block:select', initAllTabSliders);
    }
  }

  // Initial load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initAllTabSliders();
      handleThemeEditorUpdates();
    });
  } else {
    initAllTabSliders();
    handleThemeEditorUpdates();
  }

  // Use MutationObserver for theme editor updates
  if (window.Shopify?.designMode) {
    const observer = new MutationObserver(() => {
      initAllTabSliders();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
})();

